import { create } from 'zustand';

import { ApiError, api } from '../lib/api';

/**
 * Rewinds, as an overlay on whatever the server last said.
 *
 * The same shape as `stores/favorites` and for the same reason: one track can
 * be on screen in a feed row, on its own page and in the dock at once, and
 * rewinding it anywhere has to update all three.
 *
 * Kept as a separate store rather than folded into favourites because the two
 * are different acts with different rules - a favourite is private and works on
 * anything you can see; a rewind is publishing and only works on public tracks.
 * One store holding both would need a discriminator on every call.
 */
interface RepostState {
  overlay: Record<number, boolean>;
  counts: Record<number, number>;
  pending: Record<number, true>;
  error: string | null;

  toggle: (trackId: number, currently: boolean, currentCount: number) => Promise<void>;
  clear: () => void;
}

export const useReposts = create<RepostState>((set, get) => ({
  overlay: {},
  counts: {},
  pending: {},
  error: null,

  toggle: async (trackId, currently, currentCount) => {
    // A second click while the first is in flight is a double click, not a
    // second intention.
    if (get().pending[trackId]) return;

    const next = !currently;
    const optimistic = Math.max(0, currentCount + (next ? 1 : -1));

    set((state) => ({
      overlay: { ...state.overlay, [trackId]: next },
      counts: { ...state.counts, [trackId]: optimistic },
      pending: { ...state.pending, [trackId]: true },
      error: null,
    }));

    try {
      const result = await (next
        ? api.post<{ reposted: boolean; repost_count: number }>(`/tracks/${trackId}/repost`, {})
        : api.delete<{ reposted: boolean; repost_count: number }>(`/tracks/${trackId}/repost`));

      set((state) => ({
        overlay: { ...state.overlay, [trackId]: result.reposted },
        counts: { ...state.counts, [trackId]: result.repost_count },
      }));
    } catch (error) {
      set((state) => ({
        overlay: { ...state.overlay, [trackId]: currently },
        counts: { ...state.counts, [trackId]: currentCount },
        /*
         * 401 stays quiet - the button sends people to sign in rather than
         * surfacing a failure. 409 is the "only public tracks" rule, which is
         * worth saying because it is not obvious from the button.
         */
        error:
          error instanceof ApiError && error.status === 401
            ? null
            : error instanceof ApiError && error.status === 409
              ? error.message
              : 'That did not save. Try again in a moment.',
      }));
    } finally {
      set((state) => {
        const pending = { ...state.pending };
        delete pending[trackId];

        return { pending };
      });
    }
  },

  // Called on sign-out, so the next person on this browser does not inherit
  // the last one's state.
  clear: () => set({ overlay: {}, counts: {}, pending: {}, error: null }),
}));
